import React, { useState } from 'react';
import './Templates.css';

const Templates = () => {
  const [templates, setTemplates] = useState([
    {
      id: 1,
      name: 'Welcome Message',
      category: 'Marketing',
      status: 'Approved',
      language: 'en',
      created: '2024-01-15',
      content: 'Hello {{1}}! Welcome to Summit Visio. How can we help you today?'
    },
    {
      id: 2,
      name: 'Order Confirmation',
      category: 'Utility',
      status: 'Approved',
      language: 'en',
      created: '2024-01-10',
      content: 'Your order #{{1}} has been confirmed. Total: {{2}}. Expected delivery: {{3}}'
    },
    {
      id: 3,
      name: 'Appointment Reminder',
      category: 'Utility',
      status: 'Pending',
      language: 'en',
      created: '2024-01-08',
      content: 'Reminder: Your appointment with {{1}} is scheduled for {{2}} at {{3}}'
    }
  ]);

  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    category: 'Marketing',
    language: 'en',
    content: '',
    variables: []
  });

  const categories = ['Marketing', 'Utility', 'Authentication'];
  const languages = [
    { code: 'en', name: 'English' },
    { code: 'es', name: 'Spanish' },
    { code: 'fr', name: 'French' },
    { code: 'de', name: 'German' },
    { code: 'pt', name: 'Portuguese' }
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const extractVariables = (content) => {
    const matches = content.match(/\{\{(\d+)\}\}/g);
    if (matches) {
      const variables = matches.map((match, index) => ({
        id: index + 1,
        placeholder: `Variable ${index + 1}`,
        type: 'text'
      }));
      setFormData(prev => ({
        ...prev,
        variables,
        content
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        variables: [],
        content
      }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    const newTemplate = {
      id: templates.length + 1,
      ...formData,
      status: 'Pending',
      created: new Date().toISOString().split('T')[0]
    };
    
    setTemplates(prev => [newTemplate, ...prev]);
    setShowForm(false);
    setFormData({
      name: '',
      category: 'Marketing',
      language: 'en',
      content: '',
      variables: []
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Approved': return '#10b981';
      case 'Pending': return '#f59e0b';
      case 'Rejected': return '#ef4444';
      default: return '#6b7280';
    }
  };

  return (
    <div className="templates-container">
      <div className="templates-header">
        <h1>WhatsApp Templates</h1>
        <button 
          className="create-template-btn"
          onClick={() => setShowForm(true)}
        >
          + Create Template
        </button>
      </div>

      {/* Templates Grid */}
      <div className="templates-grid">
        {templates.map(template => (
          <div key={template.id} className="template-card">
            <div className="template-header">
              <h3>{template.name}</h3>
              <span 
                className="status-badge"
                style={{ backgroundColor: getStatusColor(template.status) }}
              >
                {template.status}
              </span>
            </div>
            
            <div className="template-meta">
              <span className="category">{template.category}</span>
              <span className="language">{template.language}</span>
              <span className="date">{template.created}</span>
            </div>
            
            <div className="template-content">
              <p>{template.content}</p>
            </div>
            
            {template.variables.length > 0 && (
              <div className="template-variables">
                <strong>Variables:</strong> {template.variables.length}
              </div>
            )}
            
            <div className="template-actions">
              <button className="edit-btn">Edit</button>
              <button className="duplicate-btn">Duplicate</button>
              <button className="delete-btn">Delete</button>
            </div>
          </div>
        ))}
      </div>

      {/* Create Template Modal */}
      {showForm && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2>Create WhatsApp Template</h2>
              <button 
                className="close-btn"
                onClick={() => setShowForm(false)}
              >
                ×
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="template-form">
              <div className="form-group">
                <label htmlFor="name">Template Name *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  placeholder="Enter template name"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="category">Category *</label>
                  <select
                    id="category"
                    name="category"
                    value={formData.category}
                    onChange={handleInputChange}
                    required
                  >
                    {categories.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="language">Language *</label>
                  <select
                    id="language"
                    name="language"
                    value={formData.language}
                    onChange={handleInputChange}
                    required
                  >
                    {languages.map(lang => (
                      <option key={lang.code} value={lang.code}>
                        {lang.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="content">Template Content *</label>
                <textarea
                  id="content"
                  name="content"
                  value={formData.content}
                  onChange={(e) => extractVariables(e.target.value)}
                  required
                  rows={6}
                  placeholder="Enter your template content. Use {{1}}, {{2}}, etc. for variables."
                />
                <small className="form-hint">
                  Use &lbrace;&lbrace;1&rbrace;&rbrace;, &lbrace;&lbrace;2&rbrace;&rbrace;, etc. for variables. Example: Hello &lbrace;&lbrace;1&rbrace;&rbrace;, your order &lbrace;&lbrace;2&rbrace;&rbrace; is ready.
                </small>
              </div>

              {formData.variables.length > 0 && (
                <div className="form-group">
                  <label>Detected Variables</label>
                  <div className="variables-list">
                    {formData.variables.map(variable => (
                      <div key={variable.id} className="variable-item">
                        <span className="variable-number">&lbrace;&lbrace;{variable.id}&rbrace;&rbrace;</span>
                        <input
                          type="text"
                          placeholder="Variable description"
                          className="variable-input"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="form-actions">
                <button 
                  type="button"
                  className="cancel-btn"
                  onClick={() => setShowForm(false)}
                >
                  Cancel
                </button>
                <button type="submit" className="submit-btn">
                  Create Template
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Templates;
